import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Upload, X } from 'lucide-react';
import { FILE_MIME_TYPES } from '@nordinsight/implant-core';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../ui/command';
import LoadingRing from '../ui-components/LoadingRing';
import { cn } from '../../lib/utils';
import { useToast } from '../../hooks/use-toast';
import {
    addFilesToFolder,
    removeFilesFromFolder,
    FileMember,
} from '../../services/api/folderService';
import {
    getDepartmentFiles,
    getUploadUrl,
    uploadFile,
} from '../../services/api/fileService';
import { getFileMimeType } from '../../utils/files';
import { FileType } from '../files/types';

const SUPPORTED_UPLOAD_MIME_TYPES = new Set<string>([
    FILE_MIME_TYPES.pdf,
    FILE_MIME_TYPES.doc,
    FILE_MIME_TYPES.docx,
    FILE_MIME_TYPES.jpeg,
    FILE_MIME_TYPES.png,
    FILE_MIME_TYPES.pptx,
    FILE_MIME_TYPES.xlsx,
]);
const UPLOAD_ACCEPT = [
    ...Array.from(SUPPORTED_UPLOAD_MIME_TYPES),
    '.doc',
    '.docx',
    '.pptx',
    '.xlsx',
].join(',');

interface ManageFilesDialogProps {
    folderId: string;
    existingFiles: FileMember[];
    canUpload: boolean;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onChanged: () => void;
}

const fileLabel = (
    file: { title?: string; filename?: string },
    fallback: string
): string => file.title ?? file.filename ?? fallback;

interface FileRowProps {
    label: string;
    variant: 'existing' | 'pending';
    actionLabel: string;
    onAction: () => void;
    loading?: boolean;
}

const FileRow = ({
    label,
    variant,
    actionLabel,
    onAction,
    loading,
}: FileRowProps) => (
    <li
        className={cn(
            'flex items-center justify-between gap-3 rounded-lg border border-subtle bg-card px-4 py-3',
            variant === 'pending' && 'border-dashed'
        )}
    >
        <div className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
            {label}
        </div>
        <Button
            variant="outline"
            size="sm"
            className={
                variant === 'existing' ? 'hover:text-destructive' : undefined
            }
            disabled={loading}
            onClick={onAction}
        >
            {loading ? (
                <LoadingRing size="small" />
            ) : (
                <>
                    <X className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                    {actionLabel}
                </>
            )}
        </Button>
    </li>
);

export const ManageFilesDialog = ({
    folderId,
    existingFiles,
    canUpload,
    open,
    onOpenChange,
    onChanged,
}: ManageFilesDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [allFiles, setAllFiles] = useState<FileType[]>([]);
    const [pendingFiles, setPendingFiles] = useState<Map<string, FileType>>(
        new Map()
    );
    const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [addPopoverOpen, setAddPopoverOpen] = useState(false);

    useEffect(() => {
        if (!open) {
            setPendingFiles(new Map());
            setPendingRemoveId(null);
            setAddPopoverOpen(false);
            return;
        }
        const load = async () => {
            setLoading(true);
            try {
                const result = await getDepartmentFiles();
                setAllFiles(result ?? []);
            } catch {
                toast({
                    title: t('folders.common.error'),
                    description: t('folders.manageFiles.toasts.loadFailed'),
                    variant: 'destructive',
                });
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [open, t, toast]);

    const linkedIds = useMemo(
        () => new Set(existingFiles.map((f) => f._id)),
        [existingFiles]
    );

    const availableFiles = allFiles.filter(
        (f) => !linkedIds.has(f._id) && !pendingFiles.has(f._id)
    );

    const addPending = (file: FileType) => {
        setPendingFiles((prev) => {
            const next = new Map(prev);
            next.set(file._id, file);
            return next;
        });
    };

    const undoPending = (fileId: string) => {
        setPendingFiles((prev) => {
            const next = new Map(prev);
            next.delete(fileId);
            return next;
        });
    };

    const handleUpload = async (files: FileList) => {
        const selectedFiles = Array.from(files);
        const hasUnsupportedFile = selectedFiles.some(
            (file) => !SUPPORTED_UPLOAD_MIME_TYPES.has(getFileMimeType(file))
        );

        if (hasUnsupportedFile) {
            toast({
                title: t('folders.common.error'),
                description: t('folders.manageFiles.toasts.uploadFailed'),
                variant: 'destructive',
            });
            return;
        }

        setUploading(true);
        try {
            for (const file of selectedFiles) {
                const mimeType = getFileMimeType(file);
                const uploadInfo = await getUploadUrl({
                    ownerType: 'department',
                    filename: file.name,
                    fileType: mimeType,
                });
                if (!uploadInfo.url || !uploadInfo.file?._id) {
                    throw new Error('Upload URL unavailable');
                }
                const result = await uploadFile(uploadInfo.url, file);
                if (result !== 'OK') {
                    throw new Error('Upload failed');
                }
                addPending(uploadInfo.file as FileType);
            }
        } catch {
            toast({
                title: t('folders.common.error'),
                description: t('folders.manageFiles.toasts.uploadFailed'),
                variant: 'destructive',
            });
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = async (fileId: string) => {
        setPendingRemoveId(fileId);
        try {
            await removeFilesFromFolder(folderId, [fileId]);
            onChanged();
        } catch {
            toast({
                title: t('folders.common.error'),
                description: t('folders.manageFiles.toasts.removeFailed'),
                variant: 'destructive',
            });
        } finally {
            setPendingRemoveId(null);
        }
    };

    const handleSubmit = async () => {
        if (pendingFiles.size === 0) return;
        setSubmitting(true);
        try {
            await addFilesToFolder(folderId, Array.from(pendingFiles.keys()));
            toast({
                title: t('folders.manageFiles.addedToast', {
                    count: pendingFiles.size,
                }),
            });
            onChanged();
            onOpenChange(false);
        } catch {
            toast({
                title: t('folders.common.error'),
                description: t('folders.manageFiles.toasts.addFailed'),
                variant: 'destructive',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const selectedCount = pendingFiles.size;
    const pendingList = Array.from(pendingFiles.values());
    const isEmpty = existingFiles.length === 0 && pendingList.length === 0;
    const unnamed = t('folders.manageFiles.unnamed');

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{t('folders.manageFiles.title')}</DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center py-8">
                        <LoadingRing size="small" />
                    </div>
                ) : (
                    <div className="min-w-0 space-y-3">
                        {isEmpty ? (
                            <p className="py-4 text-sm text-subtle-foreground">
                                {t('folders.manageFiles.emptyLinked')}
                            </p>
                        ) : (
                            <ul className="max-h-72 space-y-2 overflow-y-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                                {existingFiles.map((file) => (
                                    <FileRow
                                        key={file._id}
                                        label={fileLabel(file, unnamed)}
                                        variant="existing"
                                        actionLabel={t(
                                            'folders.manageFiles.remove'
                                        )}
                                        onAction={() => handleRemove(file._id)}
                                        loading={pendingRemoveId === file._id}
                                    />
                                ))}
                                {pendingList.map((file) => (
                                    <FileRow
                                        key={file._id}
                                        label={fileLabel(file, unnamed)}
                                        variant="pending"
                                        actionLabel={t(
                                            'folders.manageFiles.undo'
                                        )}
                                        onAction={() => undoPending(file._id)}
                                    />
                                ))}
                            </ul>
                        )}

                        <div className="flex gap-2">
                            {canUpload && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1"
                                    disabled={uploading}
                                    onClick={() =>
                                        fileInputRef.current?.click()
                                    }
                                >
                                    {uploading ? (
                                        <LoadingRing size="small" />
                                    ) : (
                                        <>
                                            <Upload
                                                className="mr-2 h-3.5 w-3.5"
                                                aria-hidden="true"
                                            />
                                            {t('folders.manageFiles.upload')}
                                        </>
                                    )}
                                </Button>
                            )}

                            {availableFiles.length > 0 && (
                                <Popover
                                    open={addPopoverOpen}
                                    onOpenChange={setAddPopoverOpen}
                                >
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="flex-1"
                                        >
                                            <Plus
                                                className="mr-2 h-3.5 w-3.5"
                                                aria-hidden="true"
                                            />
                                            {t('folders.manageFiles.addFile')}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        align="start"
                                        className="w-[var(--radix-popover-trigger-width)] p-0"
                                    >
                                        <Command>
                                            <CommandInput
                                                placeholder={t(
                                                    'folders.manageFiles.search'
                                                )}
                                            />
                                            <CommandList
                                                onWheel={(e) =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                <CommandEmpty>
                                                    {t(
                                                        'folders.manageFiles.noMatching'
                                                    )}
                                                </CommandEmpty>
                                                <CommandGroup>
                                                    {availableFiles.map(
                                                        (file) => (
                                                            <CommandItem
                                                                key={file._id}
                                                                value={`${fileLabel(file, '')} ${file._id}`}
                                                                onSelect={() => {
                                                                    addPending(
                                                                        file
                                                                    );
                                                                    setAddPopoverOpen(
                                                                        false
                                                                    );
                                                                }}
                                                            >
                                                                <span className="truncate text-sm font-medium">
                                                                    {fileLabel(
                                                                        file,
                                                                        unnamed
                                                                    )}
                                                                </span>
                                                            </CommandItem>
                                                        )
                                                    )}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                            )}
                        </div>
                    </div>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={UPLOAD_ACCEPT}
                    className="hidden"
                    onChange={(e) => {
                        if (e.target.files) handleUpload(e.target.files);
                        e.target.value = '';
                    }}
                />

                <DialogFooter className="items-center justify-between sm:justify-between">
                    <span className="text-xs text-subtle-foreground">
                        {selectedCount > 0 &&
                            t('folders.manageFiles.selected', {
                                count: selectedCount,
                            })}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            variant={selectedCount > 0 ? 'outline' : 'default'}
                            onClick={() => onOpenChange(false)}
                            disabled={submitting}
                        >
                            {selectedCount > 0
                                ? t('folders.manageFiles.cancel')
                                : t('folders.manageFiles.done')}
                        </Button>
                        {selectedCount > 0 && (
                            <Button
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <LoadingRing size="small" />
                                ) : (
                                    t('folders.manageFiles.add', {
                                        count: selectedCount,
                                    })
                                )}
                            </Button>
                        )}
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
